import React, { useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface FrostLineZonesProps {
  width?: number;
  height?: number;
}

export const FrostLineZones: React.FC<FrostLineZonesProps> = ({ width = 400, height = 400 }) => {
  const size = Math.min(width, height);

  // Generate static positions for particles
  const particlesRef = useRef(Array.from({ length: 150 }).map(() => {
    const angle = Math.random() * Math.PI * 2;
    const r = Math.random() * (size / 2 - 20) + 20; // 20 to edge
    return { angle, r, speed: (Math.random() * 0.002 + 0.001) * (200 / r) };
  }));

  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    const frostLineRadius = size * 0.3;

    // Draw Zones
    // Inner Zone (Too Hot)
    ctx.beginPath();
    ctx.arc(centerX, centerY, frostLineRadius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(234, 88, 12, 0.15)'; // Orange/Red tint
    ctx.fill();

    // Outer Zone (Cold)
    ctx.beginPath();
    ctx.arc(centerX, centerY, size, 0, Math.PI * 2);
    // Cut out inner circle by drawing backwards
    ctx.arc(centerX, centerY, frostLineRadius, Math.PI * 2, 0, true);
    ctx.fillStyle = 'rgba(56, 189, 248, 0.1)'; // Blue tint
    ctx.fill();

    // Frost Line
    ctx.beginPath();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2;
    ctx.setLineDash([5, 5]);
    ctx.arc(centerX, centerY, frostLineRadius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // Proto-Sun
    ctx.save();
    const sunPulse = Math.sin(frameCount * 0.05) * 5;
    const sunGrad = ctx.createRadialGradient(centerX, centerY, 5, centerX, centerY, 30 + sunPulse);
    sunGrad.addColorStop(0, '#FFFFFF');
    sunGrad.addColorStop(0.2, '#FDE047');
    sunGrad.addColorStop(0.5, '#EA580C');
    sunGrad.addColorStop(1, 'rgba(234, 88, 12, 0)');
    ctx.fillStyle = sunGrad;
    ctx.beginPath();
    ctx.arc(centerX, centerY, 30 + sunPulse, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Particles
    particlesRef.current.forEach(p => {
      p.angle += p.speed;
      const x = centerX + Math.cos(p.angle) * p.r;
      const y = centerY + Math.sin(p.angle) * p.r;

      ctx.beginPath();
      if (p.r < frostLineRadius) {
        // Rocky dot
        ctx.fillStyle = '#A8A29E'; // Stone grey
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fill();
      } else {
        // Icy chunk / Gas
        if (Math.random() > 0.5) {
          ctx.fillStyle = '#E0F2FE'; // Ice
          ctx.arc(x, y, 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillStyle = 'rgba(125, 211, 252, 0.4)'; // Gas wisp
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    });

    // Temp Gradient Bar (Side)
    const barWidth = 10;
    const barHeight = size * 0.8;
    const barX = 10;
    const barY = size * 0.1;
    
    const barGrad = ctx.createLinearGradient(0, barY, 0, barY + barHeight);
    barGrad.addColorStop(0, '#FFFFFF'); // Hot
    barGrad.addColorStop(0.2, '#EA580C');
    barGrad.addColorStop(0.5, '#64748B'); // Frost line temp
    barGrad.addColorStop(1, '#0284C7'); // Cold

    ctx.fillStyle = barGrad;
    ctx.fillRect(barX, barY, barWidth, barHeight);
    ctx.strokeStyle = '#334155';
    ctx.strokeRect(barX, barY, barWidth, barHeight);
    
    ctx.fillStyle = '#E2E8F0';
    ctx.font = '10px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText('HOT', barX + 15, barY + 10);
    ctx.fillText('COLD', barX + 15, barY + barHeight);

    // Labels
    ctx.fillStyle = '#F8FAFC';
    ctx.font = 'bold 12px sans-serif';
    ctx.textAlign = 'center';
    
    // Position labels appropriately
    ctx.fillText('FROST LINE', centerX, centerY - frostLineRadius - 10);
    
    ctx.fillStyle = '#EA580C';
    ctx.fillText('Too Hot', centerX, centerY - frostLineRadius / 2);
    ctx.font = '10px sans-serif';
    ctx.fillText('ice melts, only rock survives', centerX, centerY - frostLineRadius / 2 + 15);

    ctx.fillStyle = '#38BDF8';
    ctx.font = 'bold 12px sans-serif';
    ctx.fillText('Cold enough', centerX, centerY + frostLineRadius + 30);
    ctx.font = '10px sans-serif';
    ctx.fillText('ice survives, gas condenses', centerX, centerY + frostLineRadius + 45);

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width, background: '#0F172A', padding: '16px', borderRadius: '12px', color: 'white' }}>
      <div style={{ width: size, height: size, position: 'relative', borderRadius: '8px', overflow: 'hidden' }}>
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
};
